import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import {
  deleteBrief,
  fetchBriefDeals,
  fetchBriefLinks,
  fetchBriefs,
  mintBriefLink,
  revokeBriefLink,
  saveBrief,
} from './queries';

export const briefKeys = {
  all: ['ceo-briefs'] as const,
  links: ['ceo-briefs', 'links'] as const,
};

export function useBriefs() {
  const { client } = useAuth();
  return useQuery({
    queryKey: briefKeys.all,
    queryFn: () => fetchBriefs(client),
    retry: false,
  });
}

export function useBriefDeals() {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...briefKeys.all, 'deals'],
    queryFn: () => fetchBriefDeals(client),
    retry: false,
  });
}

function useActorId(): string {
  const { access } = useAuth();
  return access.status === 'approved' ? access.profile.id : '';
}

export function useSaveBrief() {
  const { client } = useAuth();
  const actorId = useActorId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { briefDate: string; body: string }) =>
      saveBrief(client, { ...input, actorId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: briefKeys.all });
    },
  });
}

export function useDeleteBrief() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteBrief(client, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: briefKeys.all });
    },
  });
}

/**
 * The brief links. Super admins only, decided in the database: to anybody
 * else list_brief_tokens returns nothing rather than raising, so this is an
 * empty list and the panel simply has nothing to draw.
 */
export function useBriefLinks() {
  const { client } = useAuth();
  return useQuery({
    queryKey: briefKeys.links,
    queryFn: () => fetchBriefLinks(client),
    retry: false,
  });
}

/**
 * Mint a link. The token comes back on the mutation result and is handed
 * straight to the reveal; nothing here caches it, and the list it invalidates
 * carries only tails, so there is no path by which a token reaches storage.
 */
export function useMintBriefLink() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (label: string) => mintBriefLink(client, label),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: briefKeys.links });
    },
  });
}

export function useRevokeBriefLink() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tail: string) => revokeBriefLink(client, tail),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: briefKeys.links });
    },
  });
}
